import {
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Skeleton,
  TextField,
} from "@mui/material"
import React, { Dispatch, FC, SetStateAction, useState } from "react"

import { Card } from "src/components/common/card"
import { Checkbox } from "src/components/common/checkbox"
import { create } from "src/helpers/bem"

import { useTranslation } from "src/hooks/translation"

import { useBooking } from "src/states/bookingDetail"
import { useBookings } from "src/states/bookings"
import { xRMApiCheckoutPermission } from "src/types/xRM"

import styles from "./ShopCartOverviewBillingOptionsCard.module.scss"

const bem = create(styles, "ShopCartOverviewBillingOptionsCard")

export type ShopCartOverviewBillingOptionsCardProps = {
  isLoading: boolean
  billingOptions: BillingOptions
  setBillingOptions: Dispatch<SetStateAction<BillingOptions>>
}

export type BillingOptions = {
  orderOnOwnAccount: boolean
  permission: xRMApiCheckoutPermission
  email: string
  emailIsValid: boolean
}

export const defaultBillingOptions: BillingOptions = {
  orderOnOwnAccount: false,
  permission: { checkoutPermission: "None" },
  email: "",
  emailIsValid: false,
}

export const ShopCartOverviewBillingOptionsCard: FC<
  ShopCartOverviewBillingOptionsCardProps
> = ({ isLoading, billingOptions, setBillingOptions }) => {
  const { booking } = useBooking()
  const { bookings } = useBookings()
  const { messages, formatMessage } = useTranslation()
  const [emailError, setEmailError] = useState("")

  const currentBooking = bookings.find((b) => b.id === booking?.id)

  const {
    email,
    orderOnOwnAccount,
    permission: { checkoutPermission, debitorName, debitorForeignName },
  } = billingOptions
  const m = messages.pages.shop.cart.checkout.billingCard

  const canChooseBilling = checkoutPermission === "Both"

  const renderBillingListLoading = () => (
    <List subheader={<Skeleton height={40} width={100} />}>
      <ListItem sx={{ display: "flex", gap: "15px" }}>
        <Skeleton height={40} width={"90%"} />
        <Skeleton height={40} width={"5%"} />
      </ListItem>
    </List>
  )

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value
    setBillingOptions({
      ...billingOptions,
      email,
      emailIsValid: validateEmail(email),
    })
    if (validateEmail(email)) {
      setEmailError("")
    } else {
      setEmailError(m.invalidEmail)
    }
  }

  const renderBillingList = () => (
    <List
      className={bem("billinglist")}
      subheader={
        <ListSubheader>
          {formatMessage(
            orderOnOwnAccount
              ? m.debitorDesc.onOwnAccount
              : m.debitorDesc.onExternalAccount,
            {
              ownCompany: debitorName ?? currentBooking?.exhibitor ?? "",
              orderNumber: currentBooking?.name ?? "",
              externalCompany: debitorForeignName ?? "",
            },
          )}
        </ListSubheader>
      }
    >
      <ListItem
        disablePadding
        secondaryAction={
          <Checkbox
            disableRipple
            checked={orderOnOwnAccount}
            disabled={!canChooseBilling}
            edge="end"
            tabIndex={-1}
            onChange={(_, checked) =>
              setBillingOptions({
                ...billingOptions,
                email: "",
                orderOnOwnAccount: checked,
              })
            }
          />
        }
      >
        <ListItemText
          className={bem("label", { "is-disabled": !canChooseBilling })}
          primary={m.debitorLabel}
        />
      </ListItem>
      {orderOnOwnAccount && (
        <ListItem disablePadding className={bem("invoice")}>
          <TextField
            required
            error={!!emailError}
            helperText={emailError}
            label={m.emailLabel}
            type="email"
            value={email}
            onChange={handleEmailChange}
          />
        </ListItem>
      )}
    </List>
  )

  return (
    <Card>
      <CardContent>
        <div className={bem()}>
          {isLoading ? renderBillingListLoading() : renderBillingList()}
        </div>
      </CardContent>
    </Card>
  )
}
